import { useEffect, useRef, useState } from "react";
import { useT } from "@/contexts/LanguageContext";

const TEAM_IMG = "https://images.pexels.com/photos/4487385/pexels-photo-4487385.jpeg?auto=compress&cs=tinysrgb&w=640&h=800";

function useCounter(target, duration = 1800, run) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, run]);
  return value;
}

export default function AboutStats({ stats }) {
  const { t } = useT();
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setRun(true)),
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const years = useCounter(stats?.years_in_business || 10, 1500, run);
  const shipments = useCounter(stats?.shipments_delivered || 8500, 2200, run);
  const quotes = useCounter(stats?.quotes_processed || 1240, 2000, run);

  return (
    <section data-testid="about-section" id="about" ref={ref} className="section-pad bg-[#070707]">
      <div className="container-mdgx grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-6 lg:col-span-7">
          <div className="text-overline mb-4">{t("about.overline")}</div>
          <h2 className="section-headline text-white">
            {t("about.headline")}
          </h2>
          <p className="text-white/70 leading-relaxed mt-8 max-w-2xl">
            {t("about.p1")}
          </p>
          <p className="text-white/70 leading-relaxed mt-5 max-w-2xl">
            {t("about.p2")}
          </p>

          <div ref={ref} className="grid grid-cols-3 gap-6 mt-12 grid-border">
            <div className="pr-6 pb-6">
              <div className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">{years}+</div>
              <div className="text-white/50 text-xs mt-2 tracking-[0.2em] uppercase">{t("about.years")}</div>
            </div>
            <div className="pl-6 pr-6 pb-6">
              <div className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">{shipments.toLocaleString()}+</div>
              <div className="text-white/50 text-xs mt-2 tracking-[0.2em] uppercase">{t("about.shipments")}</div>
            </div>
            <div className="pl-6 pb-6">
              <div className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">{quotes.toLocaleString()}+</div>
              <div className="text-white/50 text-xs mt-2 tracking-[0.2em] uppercase">{t("about.quotes")}</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-6 lg:col-span-5 relative">
          <div className="aspect-[4/5] overflow-hidden border border-white/10 grain">
            <img
              src={TEAM_IMG}
              alt="Logistics team"
              loading="lazy"
              decoding="async"
              width="640"
              height="800"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-[#D9633F] text-white px-6 py-4 max-w-[260px]">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase opacity-80">{t("about.builtBy")}</div>
            <div className="font-display font-bold text-xl mt-1 leading-tight">{t("about.builtByLine")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
